import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import IntranetLayout from './IntranetLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, X, Target, CheckCircle, Circle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const IntranetObjectifs = () => {
  const [objectives, setObjectives] = useState([]);
  const [formateurs, setFormateurs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ formateur_id: '', month: '', title: '', description: '' });
  const [filterMonth, setFilterMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [filterFormateur, setFilterFormateur] = useState('all');

  const user = JSON.parse(localStorage.getItem('admin_user') || '{}');
  const isAdmin = user.role === 'admin';
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = async () => {
    try {
      const params = new URLSearchParams();
      if (filterMonth) params.append('month', filterMonth);
      if (filterFormateur !== 'all' && isAdmin) params.append('formateur_id', filterFormateur);

      const [objRes, fRes] = await Promise.all([
        axios.get(`${API}/intranet/objectives?${params}`, { headers }),
        isAdmin ? axios.get(`${API}/intranet/formateurs`) : Promise.resolve({ data: [] }),
      ]);
      setObjectives(objRes.data);
      if (isAdmin) setFormateurs(fRes.data);
    } catch (err) { console.error(err); }
  };

  useEffect(() => { fetchData(); }, [filterMonth, filterFormateur]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/intranet/objectives`, form, { headers });
      toast.success('Objectif créé');
      setShowForm(false);
      setForm({ formateur_id: '', month: '', title: '', description: '' });
      fetchData();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Erreur'); }
  };

  const handleToggleStatus = async (id) => {
    try {
      await axios.put(`${API}/intranet/objectives/${id}/status`, {}, { headers });
      fetchData();
    } catch (err) { toast.error('Erreur'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet objectif ?')) return;
    try {
      await axios.delete(`${API}/intranet/objectives/${id}`, { headers });
      toast.success('Supprimé');
      fetchData();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Erreur'); }
  };

  const getFormateurName = (id) => {
    const f = formateurs.find(f => f.id === id);
    return f ? f.name : 'Formateur';
  };

  const monthOptions = useMemo(() => {
    const options = [];
    const now = new Date();
    for (let i = -3; i <= 3; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
      const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
      options.push({ value: val, label: label.charAt(0).toUpperCase() + label.slice(1) });
    }
    return options;
  }, []);

  // Stats
  const total = objectives.length;
  const done = objectives.filter(o => o.status === 'termine').length;
  const rate = total > 0 ? Math.round(done / total * 100) : 0;

  // Group by formateur (admin view)
  const grouped = useMemo(() => {
    if (!isAdmin) return { [user.id || 'me']: objectives };
    const groups = {};
    objectives.forEach(o => {
      if (!groups[o.formateur_id]) groups[o.formateur_id] = [];
      groups[o.formateur_id].push(o);
    });
    return groups;
  }, [objectives, isAdmin, user.id]);

  return (
    <IntranetLayout>
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h2 className="font-heading text-2xl font-extrabold text-slate-900">Objectifs mensuels</h2>
            <p className="text-sm text-slate-500">{isAdmin ? 'Définir et suivre les objectifs des formateurs' : 'Vos objectifs à atteindre ce mois'}</p>
          </div>
          {isAdmin && (
            <Button onClick={() => { setShowForm(true); setForm({ formateur_id: formateurs[0]?.id || '', month: filterMonth, title: '', description: '' }); }} className="btn-primary" data-testid="add-objective-btn">
              <Plus size={16} className="mr-1" /> Nouvel objectif
            </Button>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Select value={filterMonth} onValueChange={setFilterMonth}>
            <SelectTrigger className="w-52 rounded-xl" data-testid="filter-month"><SelectValue /></SelectTrigger>
            <SelectContent>
              {monthOptions.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
          {isAdmin && formateurs.length > 0 && (
            <Select value={filterFormateur} onValueChange={setFilterFormateur}>
              <SelectTrigger className="w-52 rounded-xl" data-testid="filter-formateur"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les formateurs</SelectItem>
                {formateurs.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Progress bar */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-slate-700">Progression : {done}/{total} objectifs terminés</span>
            <span className={`text-sm font-extrabold ${rate >= 80 ? 'text-emerald-600' : rate >= 50 ? 'text-amber-600' : 'text-slate-400'}`}>{rate}%</span>
          </div>
          <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all duration-500 ${rate >= 80 ? 'bg-emerald-500' : rate >= 50 ? 'bg-amber-500' : 'bg-[#1B6FE3]'}`} style={{ width: `${rate}%` }}></div>
          </div>
        </div>

        {/* Create Objective Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
            <div className="bg-white rounded-2xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-heading text-lg font-extrabold">Nouvel objectif</h3>
                <button onClick={() => setShowForm(false)}><X size={20} /></button>
              </div>
              <form onSubmit={handleSubmit} className="space-y-3" data-testid="objective-form">
                <div>
                  <Label className="text-xs font-semibold mb-1 block">Formateur *</Label>
                  <Select value={form.formateur_id} onValueChange={v => setForm({...form, formateur_id: v})}>
                    <SelectTrigger className="rounded-lg" data-testid="select-formateur"><SelectValue placeholder="Choisir un formateur" /></SelectTrigger>
                    <SelectContent>{formateurs.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold mb-1 block">Mois *</Label>
                  <Select value={form.month} onValueChange={v => setForm({...form, month: v})}>
                    <SelectTrigger className="rounded-lg"><SelectValue /></SelectTrigger>
                    <SelectContent>{monthOptions.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold mb-1 block">Objectif *</Label>
                  <Input required value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="rounded-lg" placeholder="Ex: Finir les activités 1-15 pour le groupe Kids" data-testid="input-title" />
                </div>
                <div>
                  <Label className="text-xs font-semibold mb-1 block">Description (optionnel)</Label>
                  <Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={3} className="rounded-lg" placeholder="Détails supplémentaires..." />
                </div>
                <Button type="submit" className="w-full btn-primary justify-center" data-testid="submit-objective">Créer l'objectif</Button>
              </form>
            </div>
          </div>
        )}

        {/* Objectives list */}
        {total === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-10 text-center text-slate-400">
            {isAdmin ? 'Aucun objectif défini pour ce mois. Cliquez sur "Nouvel objectif" pour commencer.' : 'Aucun objectif assigné pour ce mois.'}
          </div>
        ) : (
          Object.entries(grouped).map(([formateurId, objs]) => (
            <div key={formateurId} className="space-y-3">
              {isAdmin && (
                <div className="flex items-center gap-2 mt-2">
                  <div className="w-8 h-8 bg-[#1B6FE3]/10 rounded-lg flex items-center justify-center">
                    <Target size={16} className="text-[#1B6FE3]" />
                  </div>
                  <div>
                    <p className="font-heading text-sm font-extrabold text-slate-800">{getFormateurName(formateurId)}</p>
                    <p className="text-xs text-slate-500">{objs.filter(o => o.status === 'termine').length}/{objs.length} terminés</p>
                  </div>
                </div>
              )}
              {objs.map(obj => (
                <div key={obj.id} className={`bg-white rounded-xl border p-4 flex items-start gap-3 transition-all ${obj.status === 'termine' ? 'border-emerald-200 bg-emerald-50/30' : 'border-slate-200'}`} data-testid={`objective-${obj.id}`}>
                  <button onClick={() => handleToggleStatus(obj.id)} className="mt-0.5 flex-shrink-0" data-testid={`toggle-${obj.id}`}>
                    {obj.status === 'termine' ? (
                      <CheckCircle size={22} className="text-emerald-500" />
                    ) : (
                      <Circle size={22} className="text-slate-300 hover:text-[#1B6FE3]" />
                    )}
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className={`font-semibold text-sm ${obj.status === 'termine' ? 'text-slate-500 line-through' : 'text-slate-900'}`}>{obj.title}</p>
                    {obj.description && <p className="text-xs text-slate-500 mt-0.5">{obj.description}</p>}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${obj.status === 'termine' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                      {obj.status === 'termine' ? 'Terminé' : 'En cours'}
                    </span>
                    {isAdmin && <button onClick={() => handleDelete(obj.id)} className="text-red-400 hover:text-red-600 hover:bg-red-50 p-1 rounded-lg ml-1"><Trash2 size={14} /></button>}
                  </div>
                </div>
              ))}
            </div>
          ))
        )}
      </div>
    </IntranetLayout>
  );
};

export default IntranetObjectifs;
